/**
 * Shared hook for activity notification data and actions
 *
 * Extracted from HeaderActivityPopoverContainer so both the header
 * popover (mobile) and SidePanel (web sidebar) can consume the same
 * activity feed query, unread count, and mark-all-read mutation
 * without duplicating logic.
 * @module hooks/useActivityNotifications
 */

import type { ApolloCache } from "@apollo/client";
import { useCallback, useEffect, useMemo, useRef } from "react";

import {
  ListActivitiesFeedDocument,
  ListActivitiesFeedQuery,
  useListActivitiesFeedQuery,
  useMarkActivitiesAsReadMutation,
} from "@/generated/graphql";
import { useToastNotification } from "@/hooks/useToastNotification";
import {
  addDismissedActivityIds,
  loadDismissedActivityIds,
  useDismissedActivityIds,
} from "@/utils/dismissedActivities";

/**
 * Return type for useActivityNotifications hook
 */
interface UseActivityNotificationsReturn {
  /** Raw activity edge array from the query */
  readonly activityEdges: readonly {
    readonly node: {
      readonly activityId: string;
      readonly isRead: boolean;
    };
  }[];
  /** Number of unread activities (excluding dismissed) */
  readonly unreadActivityCount: number;
  /** Handler to mark all unread activities as read */
  readonly handleMarkAllActivity: () => void;
}

/**
 * Sets isRead: true on all cached UserActivity entities across both bell (first:15)
 * and What's New (first:50) query variants so the UI updates without refetching.
 * @param cache - Apollo cache instance provided by the mutation update callback
 */
const updateCacheAfterMarkAllRead = (cache: ApolloCache<unknown>) => {
  const cachedBell = cache.readQuery<ListActivitiesFeedQuery>({
    query: ListActivitiesFeedDocument,
    variables: { first: 15 },
  });
  const cachedWhatsNew = cache.readQuery<ListActivitiesFeedQuery>({
    query: ListActivitiesFeedDocument,
    variables: { first: 50 },
  });
  const allEdges = [
    ...(cachedBell?.listMyActivities?.edges ?? []),
    ...(cachedWhatsNew?.listMyActivities?.edges ?? []),
  ];
  const uniqueNodeIds = allEdges.reduce<Record<string, true>>((acc, edge) => {
    const id = cache.identify(edge.node);
    return id && !(id in acc) ? { ...acc, [id]: true } : acc;
  }, {});
  Object.keys(uniqueNodeIds).forEach(id => {
    cache.modify({
      id,
      fields: {
        isRead() {
          return true;
        },
      },
    });
  });
};

/**
 * Provides activity notification data and mark-as-read actions
 *
 * Polls for activities every 25s, tracks dismissed IDs across sessions
 * via AsyncStorage, and provides a handler to mark all unread activities as read.
 * @returns Activity edges, unread count, and mark-all-read handler
 * @remarks Hydrates dismissed IDs from AsyncStorage once on mount so
 * dismissals survive full page reloads (SE-4096).
 */
function useActivityNotifications(): UseActivityNotificationsReturn {
  const toast = useToastNotification();

  const { data: activityData } = useListActivitiesFeedQuery({
    variables: { first: 15 },
    pollInterval: 25000,
  });

  const activityEdges = useMemo(
    () => activityData?.listMyActivities?.edges ?? [],
    [activityData?.listMyActivities?.edges]
  );

  const dismissedIds = useDismissedActivityIds();

  const unreadActivityCount = useMemo(
    () =>
      activityEdges.filter(
        edge => !edge.node.isRead && !dismissedIds.has(edge.node.activityId)
      ).length,
    [activityEdges, dismissedIds]
  );

  const unreadActivityIds = useMemo(
    () =>
      activityEdges.reduce<readonly string[]>(
        (acc, edge) =>
          edge.node.isRead || dismissedIds.has(edge.node.activityId)
            ? acc
            : [...acc, edge.node.activityId],
        []
      ),
    [activityEdges, dismissedIds]
  );

  const [markActivitiesAsRead] = useMarkActivitiesAsReadMutation({
    onCompleted: () =>
      toast.show({
        action: "success",
        description: "You\u2019ve marked all activities as read",
      }),
    onError: err =>
      toast.show({ action: "danger", description: err?.message || "" }),
  });

  const handleMarkAllActivity = useCallback(() => {
    if (unreadActivityIds.length === 0) {
      return;
    }
    addDismissedActivityIds([...unreadActivityIds]);
    markActivitiesAsRead({
      variables: { input: { activityIds: [...unreadActivityIds] } },
      update: updateCacheAfterMarkAllRead,
    });
  }, [markActivitiesAsRead, unreadActivityIds]);

  const hasHydrated = useRef(false);
  useEffect(() => {
    if (!hasHydrated.current) {
      hasHydrated.current = true;
      loadDismissedActivityIds();
    }
  }, []);

  return {
    activityEdges,
    unreadActivityCount,
    handleMarkAllActivity,
  };
}

export default useActivityNotifications;
export { updateCacheAfterMarkAllRead };
export type { UseActivityNotificationsReturn };
